/**
 * Shape menu parser output for DB import (matches generate-menu-from-pdf.js):
 * { categories: [{ name, items: [{ name, description, price }] }] }
 * price = number in dollars (not cents).
 */

const DEFAULT_CATEGORY_NAME = "Menu";
const MAX_NAME_LEN = 255;

/** @returns {number} Price in dollars, 2 decimal places; 0 if unknown. */
export function normalizeImportPrice(item) {
  let raw = item?.price;
  if (raw == null && item?.prices) {
    raw = item.prices.serves10 ?? item.prices.serves20 ?? null;
  }
  const num = Number(raw);
  if (!Number.isFinite(num) || num < 0) return 0;
  return Number(num.toFixed(2));
}

function categoryKeyForItem(item) {
  const raw =
    item?.category ||
    item?.section ||
    item?.menuCategory ||
    item?.menuSection ||
    "";
  const name = String(raw).trim();
  return name ? name.slice(0, MAX_NAME_LEN) : DEFAULT_CATEGORY_NAME;
}

function formatModifierGroups(groups) {
  if (!Array.isArray(groups) || !groups.length) return "";

  const lines = [];
  for (const group of groups) {
    const title = String(group?.name || "Options").trim();
    if (!title) continue;
    lines.push(`${title}:`);

    const options = Array.isArray(group.options) ? group.options : [];
    for (const opt of options) {
      const optName = String(opt?.name || "").trim();
      if (!optName) continue;

      if (opt.prices && typeof opt.prices === "object") {
        const parts = [];
        if (opt.prices.whole != null) parts.push(`whole $${opt.prices.whole}`);
        if (opt.prices.sideA != null) parts.push(`side A $${opt.prices.sideA}`);
        if (opt.prices.sideB != null) parts.push(`side B $${opt.prices.sideB}`);
        if (opt.prices.default != null) parts.push(`$${opt.prices.default}`);
        lines.push(`  ${optName}${parts.length ? ` (${parts.join(", ")})` : ""}`);
      } else if (opt.price != null && Number.isFinite(Number(opt.price))) {
        lines.push(`  ${optName} — $${Number(opt.price).toFixed(2)}`);
      } else {
        lines.push(`  ${optName}`);
      }
    }
  }

  return lines.join("\n").trim();
}

function buildImportDescription(item) {
  const base = String(item?.ingredients || "").trim();
  const mods = formatModifierGroups(item?.modifierGroups);
  if (base && mods) return `${base}\n\n${mods}`;
  if (mods) return mods;
  return base;
}

/**
 * @param {Array<object>} items — flat parser items
 * @returns {{ name: string, items: { name: string, description: string, price: number }[] }[]}
 */
export function itemsToImportCategories(items) {
  const buckets = new Map();

  for (const item of items || []) {
    const name = String(item?.name || "").trim().slice(0, MAX_NAME_LEN);
    if (!name) continue;

    const categoryName = categoryKeyForItem(item);
    if (!buckets.has(categoryName)) {
      buckets.set(categoryName, []);
    }

    buckets.get(categoryName).push({
      name,
      description: buildImportDescription(item),
      price: normalizeImportPrice(item),
    });
  }

  if (!buckets.size) {
    return [{ name: DEFAULT_CATEGORY_NAME, items: [] }];
  }

  return [...buckets.entries()].map(([name, catItems]) => ({
    name: name || DEFAULT_CATEGORY_NAME,
    items: catItems,
  }));
}

/**
 * Full JSON document shown in /menu-parser and downloaded as menu-items.json.
 */
export function buildRestaurantMenuImportJson({
  items,
  source,
  sourceName,
  parser,
  provider,
  pipelineMeta,
}) {
  const categories = itemsToImportCategories(items);
  const productCount = categories.reduce(
    (sum, cat) => sum + (cat.items?.length || 0),
    0
  );

  return {
    schema: "restogpt-menu-import-v1",
    source: source || null,
    sourceName: sourceName || null,
    parsedAt: new Date().toISOString(),
    parser: parser || "unknown",
    ...(provider ? { aiProvider: provider } : {}),
    ...(pipelineMeta ? { pipelineMeta } : {}),
    categoryCount: categories.length,
    productCount,
    itemCount: productCount,
    categories,
  };
}
